// client/src/components/modals/ConfirmDeleteModal.jsx
import React from "react";
import { FaTimes, FaTrash, FaExclamationTriangle } from "react-icons/fa";

/**
 * ConfirmDeleteModal - Verification modal for inactivating media files
 * 
 * This modal appears when users select media and click the Delete button.
 * It confirms the action before setting selected media records to inactive (active = 0).
 * 
 * @param {boolean} isOpen - Controls modal visibility
 * @param {function} onClose - Callback to close modal without action
 * @param {function} onConfirm - Callback to confirm deletion (sets active = 0)
 * @param {number} count - Number of selected media items
 * @param {string} context - Context for the deletion ('event' or 'event-item')
 */
const ConfirmDeleteModal = ({ isOpen, onClose, onConfirm, count = 0, context = "event" }) => {
  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          maxWidth: "480px",
          width: "100%",
          padding: "24px",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div
              style={{
                width: "48px",
                height: "48px",
                borderRadius: "50%",
                backgroundColor: "#fee",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#c00",
              }}
            >
              <FaExclamationTriangle size={24} />
            </div>
            <h2
              style={{
                margin: 0,
                fontSize: "20px",
                fontWeight: "600",
                color: "#1a1a1a",
              }}
            >
              Confirm Delete
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#666",
              fontSize: "20px",
            }}
            aria-label="Close modal"
          >
            <FaTimes />
          </button>
        </div>

        {/* Body */}
        <div style={{ marginBottom: "24px" }}>
          <p
            style={{
              margin: "0 0 12px 0",
              fontSize: "16px",
              lineHeight: "1.5",
              color: "#333",
            }}
          >
            Are you sure you want to delete{" "}
            <strong>
              {count} {count === 1 ? "media file" : "media files"}
            </strong>
            ?
          </p>
          <p
            style={{
              margin: "0",
              fontSize: "14px",
              lineHeight: "1.5",
              color: "#666",
            }}
          >
            {context === "event-item"
              ? "This will hide the selected media from this event item. You can undo this by re-uploading the files."
              : "This will hide the selected media from this event. You can undo this by re-uploading the files."}
          </p>
        </div>

        {/* Footer Buttons */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
          }}
        >
          <button
            onClick={onClose}
            className="btn btn-outline"
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
              color: "#333",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#f5f5f5";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#fff";
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onConfirm();
              onClose();
            }}
            className="btn btn-danger"
            style={{
              padding: "10px 20px",
              fontSize: "14px",
              fontWeight: "500",
              borderRadius: "6px",
              border: "none",
              backgroundColor: "#dc2626",
              color: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#b91c1c";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#dc2626";
            }}
          >
            <FaTrash size={14} />
            Delete {count > 1 ? `(${count})` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDeleteModal;